import data from "../../shared/sharedData";

export default {
  name: "Attorney or Agent Information (if applicable)",
  prefix: "E",
  pageInfo: {
    text: `
    <b><u>Important Note</b></u>:  The attorney/agent information in this section, specifically the name, telephone number, 
    and email address, must be different from the employer’s point of contact information in Section D, unless the attorney or 
    agent is an employee of the employer.
    `,
    coloring: "grey",
  },
  sections: [
    {
      name: "Type of Representation",
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Attorney/Agent profile to populate this section (optional)",
          code: "E",
          prefill: {
            schema: "agentAttorney",
            type: "agent-attorney-individs",
            formName: "9142B",
            formSection: "E",
          },
        },
        {
          isGroup: {
            code: "E.1",
            machine_name: "attyRepresentType",
            label:
              "Indicate the type of representation for the employer in the filing of this application. *",
            tooltip:
              "Identify whether the employer is represented by an attorney or agent in the process of filing this application. If “Attorney” or “Agent” is selected, complete the remainder of Section E.",
            coloring: "none",
            fields: [
              {
                widget: "radio",
                choice: "Attorney",
                value: "Attorney",
                validate: ["required"],
                clearSchema: ["agntMspaAttached"],
                clearFiles: [
                  "attyIsAgreementAttached",
                  "e21a_is_mspa_uploaded",
                  //not updated
                ],
              },
              {
                widget: "radio",
                choice: "Agent",
                value: "Agent",
                validate: ["required"],
                clearSchema: [
                  "attyStatebarno",
                  "attyStatehighct",
                  "attyNamehighct",
                ],
              },
              {
                widget: "radio",
                choice: "None",
                value: "None",
                validate: ["required"],
                clearSchema: [
                  "attyRepresentType",
                  "attyLastname",
                  "attyFirstname",
                  "attyMiddlename",
                  "attyAddr1",
                  "attyAddr2",
                  "attyCity",
                  "attyState",
                  "attyPostcode",
                  "attyCountry",
                  "attyProvince",
                  "attyPhone",
                  "attyPhoneext",
                  "attyEmail",
                  "attyBizname",
                  "attyFein",
                  "attyStatebarno",
                  "attyStatehighct",
                  "attyNamehighct",
                  "attyIsAgreementAttached",
                  "agntMspaAttached",
                ],
                clearFiles: [
                  "attyIsAgreementAttached",
                  "e21a_is_mspa_uploaded",
                  //not updated
                ],
              },
            ],
          },
        },
      ],
    },
    {
      isVisible: {
        when: "attyRepresentType",
        is: ["Attorney", "Agent"],
        operator: "||",
      },
      name: "Attorney / Agent Name",
      fields: [
        {
          code: "E.2",
          machine_name: "attyLastname",
          widget: "text",
          label: "Attorney or Agent's Last (family) Name",
          validate: [
            "required",
            // {
            //   minLength: 2,
            //   message: "Attorney or Agent's Last Name must contain at least 2 characters.",
            // },
          ],
          warning: {
            text: `In accordance with OMB approved instructions, the attorney/agent information contained in this section, 
            specifically the name, telephone number, and email address, must be different from the employer's point of contact 
            information in Section C of this application, unless the person named in this section is an employee of the employer.`,
            isVisible: {
              when: "emppocLastname",
              is: "attyLastname",
              operator: "compareFieldValues",
            },
          },
          maxLength: 35,
          tooltip: "Enter the last (family) name of the attorney/agent.",
        },
        {
          code: "E.3",
          machine_name: "attyFirstname",
          widget: "text",
          label: "First (given) Name",
          validate: ["required"],
          maxLength: 35,
          warning: {
            text: `Please verify the information entered for accuracy. In accordance with OMB approved instructions, the attorney/agent 
            information contained in this section, specifically the name, telephone number, and email address, must be different 
            from the employer's point of contact information in Section C of this application, unless the person named in this 
            section is an employee of the employer.`,
            isVisible: {
              when: "attyLastname attyFirstname",
              is: ["emppocLastname", "emppocFirstname"],
              operator: "compareMultiFieldValues",
            },
          },
          tooltip: "Enter the first (given) name of the attorney/agent.",
        },
        {
          code: "E.4",
          machine_name: "attyMiddlename",
          widget: "text",
          label: "Middle name(s)",
          maxLength: 35,
          tooltip:
            "Enter the middle name(s) of the attorney/agent, if applicable. If the attorney/agent does not have a middle name, enter “N/A.”",
        },
      ],
    },
    {
      isVisible: {
        when: "attyRepresentType",
        is: ["Attorney", "Agent"],
        operator: "||",
      },
      name: "Attorney / Agent Address",
      fields: [
        {
          code: "E.5",
          machine_name: "attyAddr1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full street address.",
            },
          ],
          maxLength: 60,
          tooltip: "Enter the business street address of the attorney/agent.",
        },
        {
          code: "E.6",
          machine_name: "attyAddr2",
          widget: "text",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span>",
          maxLength: 60,
          tooltip:
            "If additional space is needed for the street address, use this line to complete the attorney/agent’s street address. If no additional space is needed, enter “N/A.”",
        },
        {
          code: "E.10",
          machine_name: "attyCountry",
          widget: "select",
          label: "Country",
          selection: data.country,
          clearSchema: ["attyState", "attyProvince"],
          validate: ["required"],
          tooltip: "Enter the country of the attorney/agent.",
        },
        {
          code: "E.7",
          machine_name: "attyCity",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
          ],
          maxLength: 35,
          tooltip: "Enter the city of the attorney/agent.",
        },
        {
          code: "E.8",
          machine_name: "attyState",
          widget: "select",
          label: "State",
          state: true,
          selection: data.state,
          validate: ["required"],
          isVisible: {
            when: "attyCountry",
            is: "UNITED STATES OF AMERICA",
          },
          tooltip:
            "Enter the State, District, or Territory of the attorney/agent",
        },
        {
          code: "E.9",
          machine_name: "attyPostcode",
          widget: "text",
          label: "Postal Code",
          validate: [
            {
              postalCountry: "attyCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "attyCountry",
              },
            },
            {
              isRequired: "attyCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          maxLength: 10,
          tooltip: "Enter the postal (zip) code of the attorney/agent.",
        },
        {
          code: "E.11",
          machine_name: "attyProvince",
          widget: "text",
          label: "Province",
          maxLength: 25,
          validate: [
            {
              isRequired: "attyCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
            {
              minLength: 5,
              message: "Province must contain at least 5 characters.",
            },
          ],
          tooltip:
            "Enter the province of the attorney/agent, if applicable. Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      isVisible: {
        when: "attyRepresentType",
        is: ["Attorney", "Agent"],
        operator: "||",
      },
      name: "Attorney / Agent Contact Information",
      fields: [
        {
          code: "E.12",
          machine_name: "attyPhone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          warning: {
            text: `Please verify the information entered for accuracy. In accordance with OMB approved instructions, 
            the attorney/agent information contained in this section, specifically the name, telephone number, 
            and email address, must be different from the employer's point of contact information in Section C of 
            this application, unless the person named in this section is an employee of the employer.`,
            isVisible: {
              when: "emppocPhone",
              is: "attyPhone",
              operator: "compareFieldValues",
            },
          },
          tooltip:
            "Enter the area code and telephone number of the attorney/agent. Include country code, if located outside the United States.",
        },
        {
          code: "E.13",
          machine_name: "attyPhoneext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber", //don't allow decimal Number and negative number
          maxLength: 4,
          tooltip:
            "Enter the extension of the telephone number of the attorney/agent, if applicable. Enter “N/A” if not applicable.",
        },
        {
          code: "E.14",
          machine_name: "attyEmail",
          widget: "text",
          label: "Law Firm/Business Email Address",
          validate: [
            "required",
            {
              email: {
                extraData: {
                  allowNAs: true,
                },
              },
            },
            {
              maxLength: 55,
              message: "This field cannot be more than 55 characters long.",
            },
          ],
          maxLength: 55,
          warning: {
            text: `Please verify the information entered for accuracy. In accordance with OMB approved instructions, 
            the attorney/agent information contained in this section, specifically the name, telephone number, 
            and email address, must be different from the employer's point of contact information in Section C of this application, 
            unless the person named in this section is an employee of the employer.`,
            isVisible: {
              when: "emppocEmail",
              is: "attyEmail",
              operator: "compareFieldValues",
            },
          },
          tooltip: `Enter the business e-mail address of the attorney/agent in the format name@emailaddress.top-leveldomain. 
            The email entered in this field must be the one regularly used by the attorney/agent’s point of contact 
            to send and receive electronic communications from the Department with respect to the processing of this application. 
            If the attorney/agent’s point of contact does not possess a business email address, please enter “N/A.”`,
        },
        {
          code: "E.15",
          machine_name: "attyBizname",
          widget: "text",
          label: "Law Firm/Business Name",
          validate: [
            "required",
            { minLength: 5, message: "Enter the full legal business name" },
          ],
          maxLength: 120,
          tooltip:
            "Enter the attorney/agent’s law firm or business name. The attorney/agent’s law firm or business name is the exact name that is reported to the IRS.",
        },
        {
          code: "E.16",
          machine_name: "attyFein",
          widget: "text",
          label: "Law Firm/Business FEIN",
          validate: ["fein", { minLength: 10 }],
          parse: "fein",
          maxLength: 10,
          tooltip:
            "Enter the attorney/agent's law firm or business nine-digit FEIN as assigned by the IRS. Do not enter a social security number. If not applicable, leave blank.",
        },
      ],
    },
    {
      isVisible: {
        when: "attyRepresentType",
        is: "Attorney",
      },
      name: "Attorney / Agent Identifiers",
      fields: [
        {
          code: "E.17",
          machine_name: "attyStatebarno",
          widget: "text",
          label: "State Bar Number(s)",
          validate: ["required", { minLength: 2 }],
          maxLength: 35,
          tooltip: `Enter the attorney’s State Bar number. If the attorney is licensed in more than one State, enter 
            only one State Bar number. If submitting this form electronically and the attorney is licensed in a 
            State which does not issue State Bar numbers, leave the field blank and once confirmed the field will 
            be automatically pre-populated with “N/A.” <br><br><strong>Note:</strong> The answers to questions 18 and 
            19 below should correspond to the same State for which a Bar number was provided in question 17, if any.`,
        },
        {
          code: "E.18",
          machine_name: "attyStatehighct",
          widget: "select",
          label: "State of highest court where attorney is in good standing",
          selection: data.state,
          state: true,
          validate: ["required"],
          tooltip:
            "Enter the State of the highest court where the attorney is in good standing.",
        },
        {
          code: "E.19",
          machine_name: "attyNamehighct",
          widget: "text",
          label:
            "Name of highest state court where attorney is in good standing",
          validate: ["required"],
          maxLength: 55,
          tooltip:
            "Enter the <u>name</u> of the highest court in the State where the attorney is in good standing",
        },
      ],
    },
    {
      isVisible: {
        when: "attyRepresentType",
        is: "Agent",
      },
      name: "Agent's MSPA Certificate of Registration",
      fields: [
        {
          code: "E.20",
          machine_name: "attyIsAgreementAttached",
          label:
            "Upload a copy of the current agreement of other documentation demonstrating the agent's authority to represent the employer.",
          tooltip: `Select “Yes” or “No” to indicate whether a copy of the current agreement or other documentation demonstrating the 
            agent’s authority to represent the employer in this application is attached to this application, as required by 20 CFR 655.8(a).`,
          name: "Agent Agreement",
          widget: "file_upload",
          fileTypes: [".pdf", ".png", ".jpg"],
          validate: ["required", "checkValue"],
        },
        {
          isGroup: {
            code: "E.21",
            machine_name: "agntMspaAttached", //find this proper machine name
            label: `Are you attaching a copy of the agent's current Migrant and Seasonal Agricultural Worker Protection Act 
              (MSPA) Certificate of Registration identifying the farm labor contracting activities the agent is authorized to perform?`,
            tooltip: `Select “Yes” or “No” to indicate whether a copy of the current Migrant and Seasonal Agricultural Worker 
            Protection Act (MSPA) Certificate of Registration identifying the farm labor contracting activities the agent 
            is authorized to perform is attached to this application, as required by 20 CFR 655.8(b). If the requirements 
            for a MSPA Certificate of Registration do not apply to the Agent, select “N/A.”`,
            coloring: "none",
            warning: {
              text: `Failure to upload the MSPA certificate, may result in a delay in processing. If the requirements of MSPA do not apply, select "N/A".`,
              isVisible: {
                when: "agntMspaAttached",
                is: "NO",
              },
            },
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "N/A",
                value: "N/A",
                validate: ["required"],
              },
            ],
          },
        },
        {
          isVisible: {
            when: "agntMspaAttached",
            is: "YES",
          },
          machine_name: "e21a_is_mspa_uploaded",
          name: "Agent MSPA Certificate",
          label: "Uploaded Agent MSPA Certificate",
          labelHidden: true,
          widget: "file_upload",
          fileTypes: [".pdf", ".png", ".jpg"],
          tooltip: "placeholder tooltip",
          validate: ["required", "checkValue"],
        },
        {
          code: "I5",
          machine_name: "files",
          widget: "text",
          hidden: true,
        },
      ],
    },
  ],
};
